













































































































































































import Vue from 'vue';
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators';
import { zip, sameAsConditional } from '@/validations';
import InputMaskTypes from '@/directives/input-mask/input-mask-types';
import {
  CloseFullscreenWrapper,
  FormValidation,
  OpenHelp,
  StateOptions,
  LoadingMessage,
  TriggerRefreshError,
} from '@/mixins';
import {
  FullscreenWrapper,
  FullscreenSuccess,
  FullscreenError,
  FullscreenLoading,
} from '@/components/fullscreen-wrapper-components';
import { ValidateAddress } from '@/components/validate-address-component';
import { ExtendMixins } from '@/services';
import { AddProviderFormModel, MemberProvidersResponseModel } from '@/@types/member';
import DataTableSync from 'datatablesync/src/DataTableSync.class';
import { isNil } from 'lodash';
import { EventBus } from '@/event-bus';

interface Data {
  validateAddressIsLoading: boolean;
  showNoMatches: boolean;
  addressHasChanged: boolean;
  loadingMessage: string;
  isLoading: boolean;
  showSuccessMessage: boolean;
  successMessage: string;
  showErrorMessage: boolean;
  errorMessage: string;
  refreshError: boolean;
  confirmBeforeClose: boolean;
  changeMade: boolean;
  providerModel: AddProviderFormModel;
  accountNumberHasChanged: boolean;
  storedAccountNumber: string;
}

export default ExtendMixins(
  CloseFullscreenWrapper,
  FormValidation,
  OpenHelp,
  StateOptions,
  LoadingMessage,
  TriggerRefreshError,
).extend({
  name: 'UpdateProvider',
  props: {
    params: {
      type: Object as () => any,
      required: true,
    },
  },
  data(): Data {
    return {
      validateAddressIsLoading: false,
      showNoMatches: false,
      addressHasChanged: false,
      loadingMessage: this.$options.filters.t([
        'update-provider_text_loading-message_validating-address',
        'provider_text_loading-message_validating-address',
      ]),
      isLoading: false,
      showSuccessMessage: false,
      successMessage: this.params.successMessage,
      showErrorMessage: false,
      errorMessage: this.params.errorMessage,
      refreshError: false,
      confirmBeforeClose: true,
      changeMade: false,
      providerModel: {
        name: '',
        address: {
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zipCode: '',
        },
        accountNumber: '',
        confirmAccountNumber: '',
      },
      accountNumberHasChanged: false,
      storedAccountNumber: '',
    };
  },
  beforeMount() {
    this.constructShowErrorModel('providerModel');
  },
  validations: {
    providerModel: {
      name: {
        required,
        maxLength: maxLength(40),
      },
      address: {
        addressLine1: {
          required,
          maxLength: maxLength(35),
        },
        addressLine2: {
          maxLength: maxLength(35),
        },
        city: {
          required,
          maxLength: maxLength(25),
        },
        state: {
          required,
        },
        zipCode: {
          required,
          zip,
        },
      },
      accountNumber: {
        required,
        maxLength: maxLength(20),
      },
      confirmAccountNumber: {
        required: requiredIf(function(this: any): boolean {
          return this.accountNumberHasChanged;
        }),
        sameAs: sameAsConditional('accountNumber'),
      },
    },
  },
  computed: {
    dataTableSync(): DataTableSync {
      return this.$store.getters.currentDataTableSyncInstance(
        this.params.dataTableRef,
      );
    },
    providers(): MemberProvidersResponseModel[] {
      return this.$store.getters.providers;
    },

    //      VALIDATIONS
    nameErrors(): string[] {
      const formField = 'providerModel.name';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_provider-name_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'provider_form-error_provider-name_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    addressLine1Errors(): string[] {
      const formField = 'providerModel.address.addressLine1';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_addr-1_required',
            'global_form-error_addr-1-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'provider_form-error_addr-1_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    addressLine2Errors(): string[] {
      const formField = 'providerModel.address.addressLine2';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          'provider_form-error_addr-2_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    cityErrors(): string[] {
      const formField = 'providerModel.address.city';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_city_required',
            'global_form-error_city-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'provider_form-error_city_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    stateErrors(): string[] {
      const formField = 'providerModel.address.state';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_state_required',
            'global_form-error_state-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
    zipCodeErrors(): string[] {
      const formField = 'providerModel.address.zipCode';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_zip_required',
            'global_form-error_zip-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_zip_invalid-format',
            'global_form-error_default_invalid-format',
          ],
          'zip',
        ),
      ]);
    },
    accountNumberErrors(): string[] {
      const formField = 'providerModel.accountNumber';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_account-number_required',
            'global_form-error_account-number-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'provider_form-error_account-number_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    confirmAccountNumberErrors(): string[] {
      const formField = 'providerModel.confirmAccountNumber';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_confirm-account-number_required',
            'global_form-error_confirm-account-number-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          ['provider_form-error_confirm-account-number_same-as'],
          'sameAs',
        ),
      ]);
    },
  },
  mounted() {
    this.setProviderModel();
  },
  methods: {
    tryAgain(): void {
      this.showErrorMessage = false;
      this.confirmBeforeClose = true;
    },
    deconstructAccountNumber(): void {
      this.providerModel.accountNumber = this.providerModel.accountNumber.toUpperCase();
    },
    deconstructConfirmAccountNumber(): void {
      this.providerModel.confirmAccountNumber = this.providerModel.confirmAccountNumber.toUpperCase();
    },
    setProviderModel(): void {
      const providerIndex = this.providers.findIndex((provider) => {
        return provider.providerId.toString() === this.params.providerId.toString();
      });
      this.providerModel =
        providerIndex > -1
          ? JSON.parse(
              JSON.stringify(
                Object.assign(
                  this.providerModel,
                  this.providers[providerIndex],
                ),
              ),
            )
          : null;
      this.storedAccountNumber = this.providerModel.accountNumber;
    },
    updateAccountNumber(): void {
      this.triggerBlur('providerModel.accountNumber');
      if (
        !this.accountNumberHasChanged &&
        this.providerModel.accountNumber !== this.storedAccountNumber
      ) {
        this.accountNumberHasChanged = true;
      }
    },
    constructPayload(): any {
      return {
        id: this.providerModel.providerId,
        memberId: this.$store.getters.user.memberId,
        name: this.providerModel.name
          ? this.providerModel.name.toUpperCase()
          : null,
        address: {
          addressLine1: this.providerModel.address.addressLine1
            ? this.providerModel.address.addressLine1.toUpperCase()
            : null,
          addressLine2: this.providerModel.address.addressLine2
            ? this.providerModel.address.addressLine2.toUpperCase()
            : null,
          city: this.providerModel.address.city
            ? this.providerModel.address.city.toUpperCase()
            : null,
          state: this.providerModel.address.state
            ? this.providerModel.address.state.toUpperCase()
            : null,
          zipCode: this.providerModel.address.zipCode
            ? InputMaskTypes.zip
                .deconstruct(this.providerModel.address.zipCode)
                .toUpperCase()
            : null,
        },
        accountNumber: this.providerModel.accountNumber
          ? this.providerModel.accountNumber.toUpperCase()
          : null,
      };
    },
    validateAddress(): void {
      this.showNoMatches = false;

      if (this.addressHasChanged) {
        this.isLoading = true;
        this.validateAddressIsLoading = true;
      } else {
        this.loadingMessage = this.$options.filters.t([
          'update-provider_text_loading-message',
          'provider_text_loading-message',
          'global_loading',
        ]);
        this.isLoading = true;
        this.updateProvider(this.providerModel.address);
      }
    },
    addressCancel(): void {
      this.isLoading = false;
      this.validateAddressIsLoading = false;
    },
    showNoMatchesText(): void {
      this.isLoading = false;
      this.validateAddressIsLoading = false;
      this.showNoMatches = true;
    },
    updateProvider(address): void {
      this.validateAddressIsLoading = false;

      if (address) {
        Object.assign(this.providerModel.address, address);
      }

      if (this.addressHasChanged) {
        this.$nextTick(() => {
          this.changeLoadingMessage(
            (this.$refs.fullscreenLoadingComponent as any).$refs.loadingMessage,
            'loadingMessage',
            this.$options.filters.t([
              'update-provider_text_loading-message',
              'provider_text_loading-message',
              'global_loading',
            ]),
          );
        });
      }

      this.$store
        .dispatch('updateProvider', this.constructPayload())
        .then(() => {
          this.refreshProviders();
        })
        .catch(() => {
          this.confirmBeforeClose = false;
          this.showErrorMessage = true;
          this.isLoading = false;
        });
    },
    async refreshProviders(): Promise<void> {
      try {
        if (!isNil(this.dataTableSync)) {
          await this.dataTableSync.refresh();
        } else {
          await this.$store.dispatch('getProviders');
        }
        this.confirmBeforeClose = false;
        this.showSuccessMessage = true;
        this.isLoading = false;
        EventBus.$emit('updatedProvider', {
          providerId: this.providerModel.providerId,
        });
      } catch (error) {
        this.isLoading = false;
        this.triggerRefreshError({
          message: [
            'update-provider_text_refresh-data-error-message',
            'global_connection-error',
          ],
        });
      }
    },
  },
  components: {
    FullscreenWrapper,
    FullscreenSuccess,
    FullscreenError,
    FullscreenLoading,
    ValidateAddress,
  },
});
